import { useState } from "react";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Pin, ChevronDown, Loader2 } from "lucide-react";

export default function CurrentStudyBar() {
  const { current, refresh, setCurrent } = useCurrentStudy();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!current?.id) return null;

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await api.get("/dispensations");
      setStudies(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = async (id) => {
    if (id !== current.id) {
      await setCurrent(id);
    }
    setOpen(false);
    navigate(`/dispensations/${id}`);
  };

  return (
    <div className="bg-slate-950 text-white border-b border-slate-800 sticky top-0 z-30">
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 min-w-0 flex-1">
          <Pin className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Estudio vigente</span>
          <span className="text-sm font-semibold truncate" data-testid="current-study-label">{current.label}</span>
          {!current.is_explicit && (
            <span className="hidden md:inline text-[10px] uppercase tracking-wider text-slate-500 font-mono-num">· auto (más reciente)</span>
          )}
        </div>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => (open ? setOpen(false) : handleOpen())}
            data-testid="change-current-study"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            <span className="hidden sm:inline">Cambiar estudio</span>
            <span className="sm:hidden">Cambiar</span>
            <ChevronDown className="h-3 w-3" />
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 bg-white text-slate-900 shadow-2xl rounded-lg border border-slate-200 w-[320px] max-w-[calc(100vw-2rem)] sm:w-[360px] max-h-[420px] overflow-hidden z-40">
              <div className="p-3 border-b border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Selecciona el estudio que tomará el resto de la app</div>
              </div>
              {loading ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Cargando…
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[340px]" data-testid="current-study-list">
                  {studies.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handlePick(s.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-3 border-b border-slate-50 ${s.id === current.id ? "bg-amber-50" : ""}`}
                      data-testid={`pick-study-${s.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.label}</div>
                        <div className="text-[11px] text-slate-500">{s.period_start} → {s.period_end}</div>
                      </div>
                      {s.id === current.id && <Pin className="h-3 w-3 text-amber-600 flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
